import {
   contains, each, upperFirst, existy, notExisty, isNotEmpty, absolutizeUrl, get,
   append, createElement
} from '../utils';

import { properties, methods } from '../html5media';

const APP_NAME = window.navigator.appName.toLowerCase();
const IS_IE = APP_NAME.indexOf('microsoft') !== -1 || APP_NAME.match(/trident/gi) !== null;

export default class FlashRenderer {
   /**
    * Creates an instance of FlashRenderer.
    *
    * @param {String} name name of flash plugin
    * @param {Object} options some options for flash plugin
    * @param {Array} supportedMediaTypes list of supported types
    *
    * @memberOf FlashRenderer
    */
   constructor(name, options, supportedMediaTypes) {
      this.name = name;
      this.options = options;
      this.supportedMediaTypes = supportedMediaTypes;
   }

   canPlayType(type) {
      return contains(this.supportedMediaTypes, type);
   }

   /**
    * Create the player instance and add all native events/methods/properties as possible
    *
    * @param {MediaElement} mediaElement Instance of mejs.MediaElement already created
    * @param {Object} options All the player configuration options passed through constructor
    * @param {Object[]} mediaFiles List of sources with format: {src: url, type: x/y-z}
    * @return {Object} flash api
    */
   create(mediaElement, options, mediaFiles) {
      options.logger.debug('FlashRenderer.create', options, mediaFiles);

      let flash = {},
         i,
         il;

      // store main variable
      flash.options = options;
      flash.id = mediaElement.id + '_' + flash.options.prefix;
      flash.mediaElement = mediaElement;

      // insert data
      flash.flashState = {};
      flash.flashApi = null;
      flash.flashApiStack = [];

      // mediaElements for get/set
      each(properties, assignGettersSettersForFlash(flash));

      // add mediaElements for native methods
      const _methods = methods.slice();
      _methods.push('stop');

      each(_methods, assignMethodsForFlash(flash));

      // add a ready method that Flash can call to
      window['__ready__' + flash.id] = createOnReadyFnc(flash);
      window['__event__' + flash.id] = (eventName, message) => options.bus.emit(eventName, message);

      const isVideo = get(mediaElement, 'originalNode.tagName', '').toLowerCase() === 'video';

      render(flash, mediaElement, isVideo);

      flash.hide = () => {
         flash.options.logger.debug('hiding flash plugin');
         if (isVideo) {
            flash.flashNode.style.position = 'absolute';
            flash.flashNode.style.width = '1px';
            flash.flashNode.style.height = '1px';
            try {
               flash.flashNode.style.clip = 'rect(0 0 0 0);';
            } catch (e) {
               flash.options.logger.error('error on hiding flash player', e);
            }
         }
      };
      flash.show = () => {
         flash.options.logger.debug('showing flash plugin');
         if (isVideo) {
            flash.flashNode.style.position = '';
            flash.flashNode.style.width = '';
            flash.flashNode.style.height = '';
            try {
               flash.flashNode.style.clip = '';
            } catch (e) {
               flash.options.logger.error('error on showing flash player', e);
            }
         }
      };
      flash.setSize = (width, height) => {
         flash.options.logger.debug('changing size for flash plugin', width, height);

         flash.flashNode.style.width = width;
         flash.flashNode.style.height = height;

         if (flash.flashApi !== null) {
            flash.flashApi.fire_setSize(width, height);
         }
      };

      if (isNotEmpty(mediaFiles)) {
         for (i = 0, il = mediaFiles.length; i < il; i++) {
            if (this.canPlayType(mediaFiles[i].type)) {
               flash.options.logger.debug('FLASH', 'init, set src', mediaFiles[i].src);
               flash.setSrc(mediaFiles[i].src);
               flash.load();
               break;
            }
         }
      }

      return flash;
   }
}

function assignGettersSettersForFlash(flash) {
   return propName => {
      // add to flash state that we will store
      flash.flashState[propName] = null;

      const capName = upperFirst(propName);

      flash['get' + capName] = () => {
         flash.options.logger.debug('[', flash.options.prefix, ' get]: ', propName);
         if (flash.flashApi === null) {
            return null;
         }

         if (notExisty(flash.flashApi['get_' + propName])) {
            flash.options.logger.warn('[',  flash.options.prefix, ' MISSING]: ', propName);
            return null;
         }

         var value = flash.flashApi['get_' + propName]();

         // special case for buffered to conform to HTML5's newest
         if (propName === 'buffered') {
            return {
               start: () => 0,
               end: () => value,
               length: 1
            };
         }

         return value;
      };

      flash['set' + capName] = (value) => {
         flash.options.logger.debug('[', flash.options.prefix, ' set]: ', propName, ' = ', value);

         if (propName === 'src') {
            value = absolutizeUrl(value);
         }

         // send value to Flash
         if (flash.flashApi !== null && existy(flash.flashApi['set_' + propName])) {
            flash.flashApi['set_' + propName](value);
         } else {
            // store for after "READY" event fires
            flash.flashApiStack.push({ type: 'set', propName, value });
         }
      };
   };
}

function assignMethodsForFlash(flash) {
   return methodName => {
      // run the method on the native HTMLMediaElement
      flash[methodName] = () => {
         flash.options.logger.debug('[' + flash.options.prefix + ' ' + methodName + '()]');

         if (notExisty(flash.flashApi)) {
            // store for after "READY" event fires
            flash.flashApiStack.push({ type: 'call', methodName });
         } else if (flash.flashApi['fire_' + methodName]) {
            // send call up to Flash ExternalInterface API
            try {
               flash.flashApi['fire_' + methodName]();
            } catch (e) {
               flash.options.logger.error('error occurred when calling flash api.', e);
            }
         } else {
            flash.options.logger.warn('flash', 'missing method', methodName);
         }
      };
   };
}

function createOnReadyFnc(flash) {
   return () => {
      flash.options.logger.debug('flash is ready');

      try {
         flash.flashApi = document.getElementById('__' + flash.id);

         flash.options.bus.emit('rendererready', flash);

         // do call stack
         each(flash.flashApiStack, stackItem => {
            flash.options.logger.debug('- stack', stackItem.type, stackItem);
            if (stackItem.type === 'set') {
               const capName = upperFirst(stackItem.propName);
               flash['set' + capName](stackItem.value);
            } else if (stackItem.type === 'call') {
               flash[stackItem.methodName]();
            }
         });
      } catch (e) {
         flash.options.logger.error(e);
      }
   };
}

function render(flash, mediaElement, isVideo) {
   // insert Flash object
   flash.flashWrapper = createElement('div', { class: 'rmp-flash-wrapper' });
   const options = flash.options;

   const
      autoplay = mediaElement.getAttribute('autoplay'),
      flashVars = ['uid=' + flash.id, 'autoplay=' + autoplay],
      flashHeight = isVideo ? mediaElement.originalNode.height : 1,
      flashWidth = isVideo ? mediaElement.originalNode.width : 1;

   flashVars.push('pseudostreamstart=start');
   flashVars.push('pseudostreamtype=byte');

   append(mediaElement, flash.flashWrapper);

   if (isVideo && mediaElement.originalNode !== null) {
      mediaElement.originalNode.style.display = 'none';
   }

   var settings = [];

   if (IS_IE) {
      var specialIEContainer = createElement('div');
      append(flash.flashWrapper, specialIEContainer);

      settings = [
         'classid="clsid:D27CDB6E-AE6D-11cf-96B8-444553540000"',
         'codebase="//download.macromedia.com/pub/shockwave/cabs/flash/swflash.cab"',
         'id="__' + flash.id + '"',
         'width="' + flashWidth + '"',
         'height="' + flashHeight + '"'
      ];

      if (!isVideo) {
         settings.push('style="clip: rect(0 0 0 0); position: absolute;"');
      }

      specialIEContainer.outerHTML =
         '<object ' + settings.join(' ') + '>' +
         '<param name="movie" value="' + options.flashPath + options.filename + '?x=' + new Date() + '" />' +
         '<param name="flashvars" value="' + flashVars.join('&amp;') + '" />' +
         '<param name="quality" value="high" />' +
         '<param name="bgcolor" value="#000000" />' +
         '<param name="wmode" value="transparent" />' +
         '<param name="allowScriptAccess" value="always" />' +
         '<param name="allowFullScreen" value="true" />' +
         '<div>Loading...</div>' +
         '</object>';

   } else {

      settings = [
         `id="__${flash.id}"`,
         `name="__${flash.id}"`,
         'play="true"',
         'loop="false"',
         'quality="high"',
         'bgcolor="#000000"',
         'wmode="transparent"',
         'allowScriptAccess="always"',
         'allowFullScreen="true"',
         'type="application/x-shockwave-flash"',
         'pluginspage="//www.macromedia.com/go/getflashplayer"',
         `src="${options.flashPath}${options.filename}"`,
         `flashvars="${flashVars.join('&')}"`,
         `width="${flashWidth}"`,
         `height="${flashHeight}"`
      ];

      if (!isVideo) {
         settings.push('style="clip: rect(0 0 0 0); position: absolute;"');
      }

      flash.flashWrapper.innerHTML = `<embed ${settings.join(' ')}>`;
   }

   flash.flashNode = flash.flashWrapper.lastChild;
}
